import type { ID } from "@/lib/catalog/types";
import { api } from "@/lib/apiClient";
import { slugify } from "@/lib/slugify";

export interface AttributeGroupRecord {
  id: ID;
  name: string;
  slug: string;
  /** Optional copy shown under the group name on product specification forms. */
  description?: string | null;
  sortOrder: number;
  createdAt: string;
  attributesCount?: number;
}

const LS_KEY = "inventory.attributeGroups.v1";

function dedupeById(items: AttributeGroupRecord[]): AttributeGroupRecord[] {
  const byId = new Map<ID, AttributeGroupRecord>();
  for (const item of items) {
    byId.set(item.id, item);
  }
  return Array.from(byId.values());
}

function load(): AttributeGroupRecord[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as AttributeGroupRecord[];
    return Array.isArray(parsed) ? dedupeById(parsed) : [];
  } catch {
    return [];
  }
}

function save(items: AttributeGroupRecord[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(dedupeById(items)));
}

export function listAttributeGroupRecords(): AttributeGroupRecord[] {
  return load().sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name));
}

export function getAttributeGroupById(id: ID): AttributeGroupRecord | undefined {
  return load().find((g) => g.id === id);
}

type CreateAttributeGroupApiRequest = {
  name: string;
  slug: string;
  description: string | null;
  sort_order: number;
};

type UpsertAttributeGroupApiResponse = {
  message?: string;
  data?: {
    id: string;
    name: string;
    slug: string;
    description?: string | null;
    sort_order: number;
    created_at: string;
    attributes_count?: number;
  };
};

type UpdateAttributeGroupResult = {
  record: AttributeGroupRecord;
  message: string;
};

type CreateAttributeGroupResult = {
  record: AttributeGroupRecord;
  message: string;
};

type DeleteAttributeGroupApiResponse = {
  message?: string;
};

type DeleteAttributeGroupResult = {
  success: boolean;
  message: string;
};

type ListAttributeGroupsApiResponse = {
  data?: Array<{
    id: string;
    name: string;
    slug: string;
    description?: string | null;
    sort_order: number;
    created_at: string;
    attributes_count?: number;
  }>;
  meta?: {
    page?: number;
    per_page?: number;
    total?: number;
  };
};

function toAttributeGroupRecord(item: {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  sort_order: number;
  created_at: string;
  attributes_count?: number;
}): AttributeGroupRecord {
  return {
    id: item.id,
    name: item.name,
    slug: item.slug,
    description: item.description ?? null,
    sortOrder: item.sort_order,
    createdAt: item.created_at,
    attributesCount: item.attributes_count ?? 0,
  };
}

export async function syncAttributeGroupsFromApi() {
  const response = await api.get<ListAttributeGroupsApiResponse>("/api/v1/catalog/attribute-groups", {
    params: {
      page: 1,
      per_page: 200,
    },
  });
  const apiItems = response.data?.data ?? [];
  const records = apiItems.map(toAttributeGroupRecord);
  save(records);
  return listAttributeGroupRecords();
}

export async function createAttributeGroup(data: {
  name: string;
  slug?: string;
  description?: string | null;
  sortOrder?: number;
}): Promise<CreateAttributeGroupResult> {
  const items = load();
  const maxOrder = items.reduce((m, g) => Math.max(m, g.sortOrder ?? 0), 0);
  const payload: CreateAttributeGroupApiRequest = {
    name: data.name.trim(),
    slug: data.slug?.trim() ? slugify(data.slug) : slugify(data.name) || `group-${Date.now()}`,
    description: data.description?.trim() ? data.description.trim() : null,
    sort_order: data.sortOrder ?? maxOrder + 1,
  };

  const response = await api.post<UpsertAttributeGroupApiResponse>("/api/v1/catalog/attribute-groups", payload);
  const created = response.data?.data;
  if (!created) {
    throw new Error("Invalid create attribute group response.");
  }

  const record = toAttributeGroupRecord(created);

  const existingIndex = items.findIndex((item) => item.id === record.id);
  if (existingIndex >= 0) {
    items[existingIndex] = record;
  } else {
    items.push(record);
  }
  save(items);
  return {
    record,
    message: response.data?.message || "Attribute group created successfully.",
  };
}

export async function updateAttributeGroup(
  id: ID,
  data: { name: string; slug?: string; description?: string | null; sortOrder?: number },
): Promise<UpdateAttributeGroupResult | null> {
  const items = load();
  const i = items.findIndex((g) => g.id === id);
  if (i === -1) return null;

  const payload: CreateAttributeGroupApiRequest = {
    name: data.name.trim(),
    slug: data.slug?.trim() ? slugify(data.slug) : slugify(data.name) || items[i].slug,
    description:
      data.description === undefined
        ? items[i].description ?? null
        : data.description?.trim()
          ? data.description.trim()
          : null,
    sort_order: data.sortOrder ?? items[i].sortOrder,
  };

  const response = await api.put<UpsertAttributeGroupApiResponse>(`/api/v1/catalog/attribute-groups/${id}`, payload);
  const updated = response.data?.data;
  if (!updated) {
    throw new Error("Invalid update attribute group response.");
  }

  items[i] = toAttributeGroupRecord(updated);
  save(items);
  return {
    record: items[i],
    message: response.data?.message || "Attribute group updated successfully.",
  };
}

export async function deleteAttributeGroup(id: ID): Promise<DeleteAttributeGroupResult> {
  const response = await api.delete<DeleteAttributeGroupApiResponse>(`/api/v1/catalog/attribute-groups/${id}`);
  const items = load();
  const nextItems = items.filter((g) => g.id !== id);
  const deleted = nextItems.length !== items.length;
  if (deleted) {
    save(nextItems);
  }
  return {
    success: deleted,
    message: response.data?.message || "Attribute group deleted successfully.",
  };
}

export function replaceAllAttributeGroups(records: AttributeGroupRecord[]) {
  save(records);
}
